// External packages
import * as React from 'react';
import { Link } from 'react-router-dom';

// Components
import { ButtonIcon } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Forms';
import { Icon } from 'components/Icons';
import { Option, Select } from 'components/Select';
import * as Table from 'components/Table';
import { Tab, TabList, TabPanel, Tabs } from 'components/Tabs';

// Modules
import { LicenseGraph } from 'modules/ulaznice/LicenseGraph';

// Utils
import { useGetLicenses, useUpdateLicensePrinted } from 'api/license';
import { useUserRole } from 'components/ProtectedRoute';
import { formatDate } from 'utils/formatDate';

export const LicensesTable = () => {
	const [sort, setSort] = React.useState<{
		column: string;
		direction: 'asc' | 'desc';
	}>({ column: 'id', direction: 'desc' });
	const [activationMethodFilter, setActivationMethodFilter] =
		React.useState<string>('');
	const [printedFilter, setPrintedFilter] = React.useState<string>('all');
	const [activeTab, setActiveTab] = React.useState<string>('unredeemed');
	const [wrapperWidth, setWrapperWidth] = React.useState(0);

	const { isCashier, isAdmin } = useUserRole();

	const { data: Licenses, isLoading, isError } = useGetLicenses();
	const { mutate: updateLicensePrinted, isPending: isUpdatingPrinted } =
		useUpdateLicensePrinted();

	const filteredLicenses = React.useMemo(() => {
		if (!Licenses) return [];
		let result = Licenses;

		if (activeTab === 'redeemed') {
			result = result.filter((l) => l.redeemedAt);
			// Apply activation method filter only for redeemed licenses
			if (activationMethodFilter) {
				result = result.filter(
					(l) => l.activationMethod === activationMethodFilter
				);
			}
		} else if (activeTab === 'unredeemed') {
			result = result.filter((l) => !l.redeemedAt);
			if (printedFilter === 'printed') {
				result = result.filter((l) => l.printed);
			} else if (printedFilter === 'not-printed') {
				result = result.filter((l) => !l.printed);
			}
		}

		return result;
	}, [Licenses, activationMethodFilter, activeTab, printedFilter]);

	const sortedLicenses = React.useMemo(() => {
		if (!filteredLicenses) return [];
		return [...filteredLicenses].sort((a, b) => {
			let aVal: any = '';
			let bVal: any = '';
			switch (sort.column) {
				case 'id':
					aVal = a.id;
					bVal = b.id;
					break;
				case 'code':
					aVal = a.code || '';
					bVal = b.code || '';
					break;
				case 'validUntil':
					aVal = a.validUntil ? new Date(a.validUntil).getTime() : 0;
					bVal = b.validUntil ? new Date(b.validUntil).getTime() : 0;
					break;
				case 'redeemedAt':
					aVal = a.redeemedAt ? new Date(a.redeemedAt).getTime() : 0;
					bVal = b.redeemedAt ? new Date(b.redeemedAt).getTime() : 0;
					break;
				case 'customer':
					aVal = a.customer?.name || '';
					bVal = b.customer?.name || '';
					break;
				case 'activationMethod':
					aVal = a.activationMethod || '';
					bVal = b.activationMethod || '';
					break;
				default:
					return 0;
			}
			if (typeof aVal === 'string' && typeof bVal === 'string') {
				if (sort.direction === 'asc') return aVal.localeCompare(bVal);
				return bVal.localeCompare(aVal);
			} else {
				if (sort.direction === 'asc') return aVal - bVal;
				return bVal - aVal;
			}
		});
	}, [filteredLicenses, sort]);

	const wrapperRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (wrapperRef.current) {
			const width = wrapperRef.current.offsetWidth;
			setWrapperWidth(width);
		}
	}, [wrapperRef]);

	const handleTogglePrinted = (
		id: number,
		currentPrinted: boolean,
		nextPrinted: boolean
	) => {
		// Everyone can mark as printed, but only admins can unmark.
		if (!isAdmin && currentPrinted && !nextPrinted) return;

		updateLicensePrinted({ id, printed: nextPrinted });
	};

	return (
		<div className="flex flex-col h-full" ref={wrapperRef}>
			{isLoading || isError || !Licenses || Licenses.length === 0 ? (
				<div className="flex items-center justify-center h-full">
					{isLoading ? (
						<Icon name="loader" className="animate-spin size-10" />
					) : isError ? (
						<p>Greška pri učitavanju ulaznica.</p>
					) : (
						<p>Nema pronađenih ulaznica.</p>
					)}
				</div>
			) : (
				<Tabs
					selectedKey={activeTab}
					onSelectionChange={(key) => setActiveTab(key as string)}
					className="mt-4"
				>
					<TabList>
						<Tab id="unredeemed" className="flex-1 text-center">
							Neiskorištene
						</Tab>
						<Tab
							id="redeemed"
							className="flex-1 text-center"
							isDisabled={isCashier}
						>
							Iskorištene
						</Tab>
					</TabList>
					<TabPanel id="unredeemed" className="p-4">
						<div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
							<div className="ml-auto">
								<Label htmlFor="printedFilter" className="font-medium">
									Ispis
								</Label>
								<Select
									selectedKey={printedFilter}
									onSelectionChange={(key) => setPrintedFilter(String(key ?? 'all'))}
									className="w-40"
									aria-label="Filter ispisa"
								>
									<Option id="all" key="all">
										Sve
									</Option>
									<Option id="printed" key="printed">
										Ispisane
									</Option>
									<Option id="not-printed" key="not-printed">
										Neispisane
									</Option>
								</Select>
							</div>
						</div>
						<Table.TableContainer style={{ maxWidth: wrapperWidth }}>
							<Table.Table>
								<Table.TableHeader>
									<Table.Row>
										<Table.Column
											className="cursor-pointer select-none"
											onClick={() =>
												setSort((prev) => ({
													column: 'id',
													direction:
														prev.column === 'id' && prev.direction === 'asc'
															? 'desc'
															: 'asc',
												}))
											}
										>
											ID
											{sort.column === 'id' && (
												<span className="ml-1">
													{sort.direction === 'asc' ? '▲' : '▼'}
												</span>
											)}
										</Table.Column>
										<Table.Column
											className="cursor-pointer select-none"
											onClick={() =>
												setSort((prev) => ({
													column: 'code',
													direction:
														prev.column === 'code' && prev.direction === 'asc'
															? 'desc'
															: 'asc',
												}))
											}
										>
											Kod
											{sort.column === 'code' && (
												<span className="ml-1">
													{sort.direction === 'asc' ? '▲' : '▼'}
												</span>
											)}
										</Table.Column>
										<Table.Column
											className="cursor-pointer select-none"
											onClick={() =>
												setSort((prev) => ({
													column: 'validUntil',
													direction:
														prev.column === 'validUntil' &&
														prev.direction === 'asc'
															? 'desc'
															: 'asc',
												}))
											}
										>
											Vrijedi do
											{sort.column === 'validUntil' && (
												<span className="ml-1">
													{sort.direction === 'asc' ? '▲' : '▼'}
												</span>
											)}
										</Table.Column>
										<Table.Column />
										<Table.Column>Ispisana</Table.Column>
									</Table.Row>
								</Table.TableHeader>
								<Table.TableBody>
									{sortedLicenses.length === 0 ? (
										<Table.Row>
											<td colSpan={5} className="text-center italic py-4">
												Nema pronađenih ulaznica.
											</td>
										</Table.Row>
									) : (
										sortedLicenses.map((item) => (
											<Table.Row key={item.id}>
												<Table.Column>{item.id}</Table.Column>
												<Table.Column>{item.code}</Table.Column>
												<Table.Column className="whitespace-nowrap">
													{formatDate(item.validUntil)}
												</Table.Column>
												<Table.Column className="text-right">
													{item.code ? (
														<Link
															to={`/ulaznice/${item.code}`}
															target="_blank"
														>
															<ButtonIcon
																colorScheme="secondary"
																variant="outline"
															>
																<Icon name="qr" className="size-5" />
															</ButtonIcon>
														</Link>
													) : (
														<ButtonIcon
															isDisabled
															colorScheme="secondary"
															variant="outline"
														>
															<Icon name="qr" className="size-5" />
														</ButtonIcon>
													)}
												</Table.Column>
												<Table.Column className="whitespace-nowrap">
													<div className="flex items-center gap-2">
														<Checkbox
															aria-label={`Označi licencu ${item.code} kao ispisanu`}
															isSelected={!!item.printed}
															onChange={(checked) =>
																handleTogglePrinted(
																	item.id,
																	!!item.printed,
																	!!checked
																)
															}
															isDisabled={
																isUpdatingPrinted || (!!item.printed && !isAdmin)
															}
														/>
														<span
															className={`text-xs font-medium ${
																item.printed ? 'text-green-700' : 'text-gray-500'
															}`}
														>
															{item.printed ? 'Ispisana' : 'Neispisana'}
														</span>
														{item.printed && !isAdmin && (
															<span className="text-[11px] text-gray-500">
																Samo admin moze ponistiti ispis.
															</span>
														)}
													</div>
												</Table.Column>
											</Table.Row>
										))
									)}
								</Table.TableBody>
							</Table.Table>
						</Table.TableContainer>
					</TabPanel>
					<TabPanel id="redeemed" className="p-4">
						<div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
							<div className="ml-auto">
								<Label htmlFor="activationMethodFilter" className="font-medium">
									Način aktivacije
								</Label>
								<Select
									selectedKey={activationMethodFilter}
									onSelectionChange={(key) =>
										setActivationMethodFilter(String(key ?? ''))
									}
									className="w-32"
									aria-label="Način aktivacije"
									clearable
								>
									<Option id="" key="all">
										Svi
									</Option>
									<Option id="APP" key="APP">
										APP
									</Option>
									<Option id="MANUAL" key="MANUAL">
										MANUAL
									</Option>
								</Select>
							</div>
						</div>
						<Table.TableContainer
							className="mb-8"
							style={{ maxWidth: wrapperWidth }}
						>
							<Table.Table>
								<Table.TableHeader>
									<Table.Row>
										<Table.Column
											className="cursor-pointer select-none"
											onClick={() =>
												setSort((prev) => ({
													column: 'id',
													direction:
														prev.column === 'id' && prev.direction === 'asc'
															? 'desc'
															: 'asc',
												}))
											}
										>
											ID
											{sort.column === 'id' && (
												<span className="ml-1">
													{sort.direction === 'asc' ? '▲' : '▼'}
												</span>
											)}
										</Table.Column>
										<Table.Column
											className="cursor-pointer select-none"
											onClick={() =>
												setSort((prev) => ({
													column: 'code',
													direction:
														prev.column === 'code' && prev.direction === 'asc'
															? 'desc'
															: 'asc',
												}))
											}
										>
											Kod
											{sort.column === 'code' && (
												<span className="ml-1">
													{sort.direction === 'asc' ? '▲' : '▼'}
												</span>
											)}
										</Table.Column>
										<Table.Column
											className="cursor-pointer select-none"
											onClick={() =>
												setSort((prev) => ({
													column: 'redeemedAt',
													direction:
														prev.column === 'redeemedAt' &&
														prev.direction === 'asc'
															? 'desc'
															: 'asc',
												}))
											}
										>
											Iskorištena
											{sort.column === 'redeemedAt' && (
												<span className="ml-1">
													{sort.direction === 'asc' ? '▲' : '▼'}
												</span>
											)}
										</Table.Column>
										<Table.Column
											className="cursor-pointer select-none"
											onClick={() =>
												setSort((prev) => ({
													column: 'activationMethod',
													direction:
														prev.column === 'activationMethod' &&
														prev.direction === 'asc'
															? 'desc'
															: 'asc',
												}))
											}
										>
											Način aktivacije
											{sort.column === 'activationMethod' && (
												<span className="ml-1">
													{sort.direction === 'asc' ? '▲' : '▼'}
												</span>
											)}
										</Table.Column>
										<Table.Column>Ispisana</Table.Column>
									</Table.Row>
								</Table.TableHeader>
								<Table.TableBody>
									{sortedLicenses.length === 0 ? (
										<Table.Row>
											<td colSpan={5} className="text-center italic py-4">
												Nema pronađenih ulaznica.
											</td>
										</Table.Row>
									) : (
										sortedLicenses.map((item) => (
											<Table.Row key={item.id}>
												<Table.Column>{item.id}</Table.Column>
												<Table.Column>{item.code}</Table.Column>
												<Table.Column className="whitespace-nowrap">
													{formatDate(item.redeemedAt)}
												</Table.Column>
												<Table.Column>
													{item.activationMethod || '-'}
												</Table.Column>
												<Table.Column className="whitespace-nowrap">
													<div className="flex items-center gap-2">
														<Checkbox
															aria-label={`Označi licencu ${item.code} kao ispisanu`}
															isSelected={!!item.printed}
															onChange={(checked) =>
																handleTogglePrinted(
																	item.id,
																	!!item.printed,
																	!!checked
																)
															}
															isDisabled={
																isUpdatingPrinted || (!!item.printed && !isAdmin)
															}
														/>
														<span
															className={`text-xs font-medium ${
																item.printed ? 'text-green-700' : 'text-gray-500'
															}`}
														>
															{item.printed ? 'Ispisana' : 'Neispisana'}
														</span>
														{item.printed && !isAdmin && (
															<span className="text-[11px] text-gray-500">
																Samo admin moze ponistiti ispis.
															</span>
														)}
													</div>
												</Table.Column>
											</Table.Row>
										))
									)}
								</Table.TableBody>
							</Table.Table>
						</Table.TableContainer>
						<LicenseGraph Licenses={sortedLicenses} />
					</TabPanel>
				</Tabs>
			)}
		</div>
	);
};
